// Master-only outbox admin. Triage dead-letter, drift detection, and
// forced retries from the UI instead of curl.
//
//   GET    /admin/outbox            → totals + oldest_pending
//   GET    /admin/outbox/jobs?state → list jobs (payloads redacted)
//   POST   /admin/outbox/:id/retry  → reset job to pending (audited; reason required)
//   DELETE /admin/outbox/:id        → drop a job (audited; reason required)
//
// P0-3 (Agent B):
//   - summary cards from the totals endpoint
//   - state filter dropdown
//   - row expansion shows redacted payload + last controller result
//   - retry / delete modals require a reason
//   - "oldest pending" stale-job warning chip
//   - link to affected resource (employees/schedules) where possible

import { Fragment, useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';

type JobState = 'pending' | 'running' | 'succeeded' | 'failed' | 'dead_letter';

const STATE_VALUES: JobState[] = [
  'pending',
  'running',
  'succeeded',
  'failed',
  'dead_letter',
];

interface OutboxJobRow {
  id: string;
  type: string;
  tenant_id: string;
  resource_type: string;
  resource_id: string;
  state: JobState;
  attempt_count: number;
  max_attempts: number;
  next_run_at: string;
  last_error: string | null;
  created_at: string;
  payload?: unknown;
  controller_result_json?: unknown;
}

interface OutboxSummary {
  totals: Partial<Record<JobState, number>>;
  oldest_pending: {
    id: string;
    type: string;
    next_run_at: string;
    attempt_count: number;
  } | null;
}

const STATE_TONE: Record<JobState, string> = {
  pending: 'bg-tertiary-fixed text-on-tertiary-fixed',
  running: 'bg-primary-container text-on-primary',
  succeeded: 'bg-secondary-container text-on-secondary-container',
  failed: 'bg-error-container text-on-error-container',
  dead_letter: 'bg-error-container text-on-error-container',
};

/** Convert a resource_type + resource_id into a portal link when known. */
function linkForResource(type: string, id: string): string | null {
  switch (type) {
    case 'employee':
      return `/employees?focus=${encodeURIComponent(id)}`;
    case 'schedule':
      return `/schedules?focus=${encodeURIComponent(id)}`;
    case 'access_group':
      return `/access-groups?focus=${encodeURIComponent(id)}`;
    case 'tenant':
      return `/tenants?focus=${encodeURIComponent(id)}`;
    default:
      return null;
  }
}

export function OutboxPage() {
  const [searchParams] = useSearchParams();
  const [summary, setSummary] = useState<OutboxSummary | null>(null);
  const [jobs, setJobs] = useState<OutboxJobRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // UX-004/UX-014: seed the state filter from the URL so a link can open this
  // page already narrowed (the dashboard's failed-sync alert does). Anything
  // unrecognised falls back to 'all' rather than silently filtering to nothing.
  // Read once on mount — the dropdown owns the filter from then on, so the
  // operator's choice is not clobbered by a re-render.
  const [stateFilter, setStateFilter] = useState<JobState | 'all'>(() => {
    const requested = searchParams.get('state');
    return STATE_VALUES.includes(requested as JobState) ? (requested as JobState) : 'all';
  });
  const [expanded, setExpanded] = useState<string | null>(null);
  const [actionTarget, setActionTarget] = useState<{
    job: OutboxJobRow;
    kind: 'retry' | 'delete';
  } | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const resourceTypeFilter = (searchParams.get('resource_type') ?? '').trim();
  const resourceIdFilter = (searchParams.get('resource_id') ?? '').trim();

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setJobs(null);
    setSummary(null);
    setError(null);
    const q = new URLSearchParams({ limit: '200' });
    if (stateFilter !== 'all') q.set('state', stateFilter);
    if (resourceTypeFilter.length > 0) q.set('resource_type', resourceTypeFilter);
    if (resourceIdFilter.length > 0) q.set('resource_id', resourceIdFilter);
    const url = `/api/v1/admin/outbox/jobs?${q.toString()}`;
    Promise.all([
      apiClient.get<{ data: OutboxSummary }>('/api/v1/admin/outbox', {
        skipTenantScope: true,
      }),
      apiClient.get<{ data: OutboxJobRow[] }>(url, { skipTenantScope: true }),
    ])
      .then(([s, j]) => {
        if (cancelled) return;
        setSummary(s.data);
        setJobs(j.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load outbox');
      });
    return () => {
      cancelled = true;
    };
  }, [stateFilter, reloadTick, resourceIdFilter, resourceTypeFilter]);

  const oldestPendingAgeMs = useMemo(() => {
    if (!summary?.oldest_pending) return null;
    const t = new Date(summary.oldest_pending.next_run_at).getTime();
    if (Number.isNaN(t)) return null;
    return Date.now() - t;
  }, [summary]);
  const stale = oldestPendingAgeMs !== null && oldestPendingAgeMs > 15 * 60_000;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary">
            <Icon name="outbox" className="text-2xl" />
          </div>
          <div>
            <h1 className="font-headline-md text-headline-md text-on-surface">
              Sync queue
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Durable retry queue for controller side effects (outbox jobs).
              Master admins only.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={reload}>
            <Icon name="refresh" className="text-sm" />
            <span className="ml-1">Refresh</span>
          </Button>
        </div>
      </header>

      {/* Summary cards */}
      <section
        aria-label="Outbox totals"
        className="grid grid-cols-2 md:grid-cols-5 gap-3"
      >
        {STATE_VALUES.map((s) => (
          <SummaryCard
            key={s}
            label={s}
            // eslint-disable-next-line security/detect-object-injection -- s is from a fixed union
            count={summary?.totals?.[s] ?? 0}
            // eslint-disable-next-line security/detect-object-injection -- s is from a fixed union
            tone={STATE_TONE[s]}
            active={stateFilter === s}
            onClick={() => setStateFilter((cur) => (cur === s ? 'all' : s))}
          />
        ))}
      </section>

      {/* Stale-job warning */}
      {stale && summary?.oldest_pending ? (
        <div
          role="alert"
          className="bg-error-container text-on-error-container rounded-lg px-4 py-2 font-body-md text-body-md flex items-center gap-2"
        >
          <Icon name="warning" />
          <span>
            Oldest pending job is{' '}
            <strong>
              {Math.round(oldestPendingAgeMs! / 60_000)} min old
            </strong>{' '}
            ({summary.oldest_pending.type}). The runner may be stuck.
          </span>
        </div>
      ) : null}

      {/* Filter chip */}
      <div className="flex items-center gap-3">
        <label
          htmlFor="state-filter"
          className="font-label-md text-label-md text-on-surface-variant uppercase"
        >
          State filter
        </label>
        <select
          id="state-filter"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value as JobState | 'all')}
          className="bg-surface border border-outline-variant rounded-lg px-3 py-1 font-body-md text-body-md"
        >
          <option value="all">All</option>
          {STATE_VALUES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {/* Jobs table */}
      <section className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden">
        {error ? (
          <div className="p-6 text-error font-body-md text-body-md" role="alert">
            {error}
          </div>
        ) : jobs == null ? (
          <div className="p-12 flex justify-center">
            <Spinner />
          </div>
        ) : jobs.length === 0 ? (
          <div className="p-12 text-center text-on-surface-variant font-body-md text-body-md">
            Outbox is empty — no controller work in this state.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface-container">
              <tr>
                <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[22%]">
                  Type
                </th>
                <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[14%]">
                  Resource
                </th>
                <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[12%]">
                  State
                </th>
                <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[10%]">
                  Attempts
                </th>
                <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3">
                  Last error
                </th>
                <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[12%]">
                  Next attempt
                </th>
                <th className="text-right font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[10%]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {jobs.map((j) => {
                const link = linkForResource(j.resource_type, j.resource_id);
                const isOpen = expanded === j.id;
                return (
                  <Fragment key={j.id}>
                    <tr
                      className="hover:bg-surface-container-low transition-colors"
                    >
                      <td className="px-4 py-3 font-body-md text-body-md text-on-surface">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setExpanded(isOpen ? null : j.id)}
                            // The row expander is an icon plus a type label
                            // that can be empty, leaving an 18px target.
                            className="inline-flex min-h-[1.5rem] min-w-[1.5rem] items-center gap-1 hover:underline"
                            aria-expanded={isOpen}
                            aria-controls={`outbox-row-${j.id}`}
                          >
                            <Icon
                              name={isOpen ? 'expand_less' : 'expand_more'}
                              className="text-sm"
                            />
                            <span className="font-code text-code">{j.type}</span>
                          </button>
                          <Link
                            to={`/outbox/${j.id}`}
                            className="inline-flex min-h-[1.5rem] items-center font-label-md text-label-md uppercase text-primary hover:underline"
                            data-testid={`outbox-row-link-${j.id}`}
                            aria-label={`Open outbox job ${j.id}`}
                          >
                            Open
                          </Link>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-body-md text-body-md">
                        {link ? (
                          <Link
                            to={link}
                            // WebKit renders `font-code` shorter than Chromium
                            // does, so this measured 17px on Safari's engine
                            // and passed on Chromium. Engine-specific, and
                            // only a WebKit run would have found it.
                            className="inline-flex min-h-[1.5rem] items-center font-code text-code text-primary hover:underline"
                          >
                            {j.resource_type}/{j.resource_id.slice(0, 8)}
                          </Link>
                        ) : (
                          <span className="font-code text-code text-on-surface-variant">
                            {j.resource_type}/{j.resource_id.slice(0, 8)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase ${STATE_TONE[j.state]}`}
                        >
                          {j.state}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-body-md text-body-md">
                        {j.attempt_count} / {j.max_attempts}
                      </td>
                      <td className="px-4 py-3">
                        {j.last_error ? (
                          <span className="font-code text-code text-error">
                            {j.last_error.slice(0, 80)}
                          </span>
                        ) : (
                          <span className="text-on-surface-variant">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-body-md text-body-md text-on-surface-variant">
                        {new Date(j.next_run_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-1">
                          <button
                            type="button"
                            onClick={() =>
                              setActionTarget({ job: j, kind: 'retry' })
                            }
                            className="px-2 py-1 rounded bg-primary-container text-on-primary font-label-md text-label-md uppercase hover:bg-opacity-90"
                            title="Retry now"
                          >
                            Retry
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setActionTarget({ job: j, kind: 'delete' })
                            }
                            className="px-2 py-1 rounded bg-error-container text-on-error-container font-label-md text-label-md uppercase hover:bg-opacity-90"
                            title="Mark manually reconciled"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isOpen ? (
                      <tr
                        id={`outbox-row-${j.id}`}
                        className="bg-surface-container-low"
                      >
                        <td colSpan={7} className="px-4 py-4">
                          <div className="grid md:grid-cols-2 gap-4">
                            <PayloadBlock label="Payload (redacted)" value={j.payload} />
                            <PayloadBlock
                              label="Last controller result"
                              value={j.controller_result_json}
                            />
                          </div>
                          <p className="mt-2 font-body-md text-body-md text-on-surface-variant">
                            id <span className="font-code text-code">{j.id}</span>
                            {' · '}tenant{' '}
                            <span className="font-code text-code">
                              {j.tenant_id.slice(0, 8)}…
                            </span>
                          </p>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      {actionTarget ? (
        <ReasonModal
          job={actionTarget.job}
          kind={actionTarget.kind}
          onCancel={() => setActionTarget(null)}
          onDone={() => {
            setActionTarget(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function SummaryCard({
  label,
  count,
  tone,
  active,
  onClick,
}: {
  label: string;
  count: number;
  tone: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      // min-w-0: a grid item also defaults to min-width:auto, so at the
      // 5-column breakpoint the widest state label pushed the row 2px wide
      // instead of the pill shrinking.
      className={`min-w-0 p-3 rounded-lg border text-left transition-colors ${
        active ? 'border-primary' : 'border-outline-variant'
      } bg-surface-container-lowest hover:bg-surface-container-low`}
    >
      <span
        className={`inline-flex max-w-full items-center truncate px-2 py-1 rounded-full font-label-md text-label-md uppercase ${tone}`}
      >
        {label}
      </span>
      <div className="mt-2 font-headline-sm text-headline-sm text-on-surface">
        {count}
      </div>
    </button>
  );
}

function PayloadBlock({ label, value }: { label: string; value: unknown }) {
  const text =
    value === undefined || value === null
      ? '—'
      : typeof value === 'string'
        ? value
        : JSON.stringify(value, null, 2);
  return (
    <div>
      <div className="font-label-md text-label-md text-on-surface-variant uppercase mb-1">
        {label}
      </div>
      <pre className="font-code text-code bg-surface border border-outline-variant rounded p-2 overflow-x-auto max-h-64">
        {text}
      </pre>
    </div>
  );
}

function ReasonModal({
  job,
  kind,
  onCancel,
  onDone,
}: {
  job: OutboxJobRow;
  kind: 'retry' | 'delete';
  onCancel: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (reason.trim().length < 3) {
      setError('Please describe why (min 3 characters).');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (kind === 'retry') {
        await apiClient.post(
          `/api/v1/admin/outbox/${job.id}/retry`,
          { reason },
          { skipTenantScope: true },
        );
      } else {
        // Master-only DELETE needs the reason in the body; the audit
        // route requires it for the per-job log entry.
        await apiClient.deleteWithBody(
          `/api/v1/admin/outbox/${job.id}`,
          { reason },
          { skipTenantScope: true },
        );
      }
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `${kind} failed`);
      setSubmitting(false);
    }
  }

  const verbCap = kind === 'retry' ? 'Retry' : 'Delete';
  const heading = kind === 'retry' ? 'Retry outbox job' : 'Delete outbox job';

  return (
    <div
      role="dialog"
      aria-labelledby="outbox-action-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/30 p-margin-mobile"
    >
      <div className="w-full max-w-[480px] bg-surface-container-lowest rounded-xl shadow-lg border border-outline-variant p-8">
        <header className="flex items-center gap-3 mb-6">
          <div
            className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${
              kind === 'retry'
                ? 'bg-primary-container text-on-primary'
                : 'bg-error-container text-on-error-container'
            }`}
          >
            <Icon name={kind === 'retry' ? 'replay' : 'delete'} />
          </div>
          <div>
            <h2
              id="outbox-action-title"
              className="font-headline-sm text-headline-sm text-on-surface"
            >
              {heading}
            </h2>
            <p className="font-body-md text-body-md text-on-surface-variant">
              {job.type} · {job.resource_type}/{job.resource_id.slice(0, 8)}…
            </p>
          </div>
        </header>
        {kind === 'delete' ? (
          <p className="mb-4 font-body-md text-body-md text-on-surface">
            This drops the job from the queue. Use only after you have
            manually reconciled the controller state. The action is
            audited with your reason.
          </p>
        ) : (
          <p className="mb-4 font-body-md text-body-md text-on-surface">
            Resets the job to <code>pending</code> with <code>next_run_at</code>{' '}
            = now. The audit record will include your reason.
          </p>
        )}
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <label
              htmlFor="outbox-reason"
              className="font-label-md text-label-md text-on-surface block"
            >
              Reason
            </label>
            <textarea
              id="outbox-reason"
              required
              minLength={3}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              className="w-full px-3 py-2 bg-surface border border-outline-variant rounded-lg font-body-md text-body-md focus:ring-2 focus:ring-primary outline-none"
              placeholder={
                kind === 'retry'
                  ? 'Investigating ticket #1234, controller back online, …'
                  : 'Manually reconciled with controller; safe to drop, …'
              }
            />
          </div>
          {error ? (
            <p role="alert" className="text-error font-body-md text-body-md">
              {error}
            </p>
          ) : null}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting}>
              {verbCap}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Note: the local deleteWithBody helper that used to live here was
// extracted to apiClient.deleteWithBody (see lib/api.ts) so other
// surfaces can share the same request/error-envelope handling.
